import { BugReport } from '@mui/icons-material'
import {
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import {
  checkDebugNotificationsAccess,
  isNotificationDebugEnabled,
  sendDebugNotification
} from '../../async/supabase/notifications'
import { HBox, VBox } from '../../elements/basic-elements'
import { ButtonSecondary } from '../../elements/form-elements'
import { authState } from '../../state/auth-state'


const NOTIFICATION_TYPES = [
  { label: 'User followed you', type: 'user_followed_you' },
  { label: 'Friend took action', type: 'friend_took_action' },
  { label: 'New action matching interests', type: 'new_action_matching_interests' },
  { label: 'New action in your area', type: 'new_action_in_your_area' }
]

type TestResult = { ok: boolean; message: string }

const NotificationDebugPanel = () => {
  const { authUser } = authState()
  const [results, setResults] = useState<Record<string, TestResult>>({})
  const [loading, setLoading] = useState<Record<string, boolean>>({})

  // The allowlist is private (server-side), so ask whether this user may see the panel.
  const [allowed, setAllowed] = useState(false)
  const email = authUser?.email
  useEffect(() => {
    let cancelled = false
    setAllowed(false)
    if (!email || !isNotificationDebugEnabled()) return
    void checkDebugNotificationsAccess().then(result => {
      if (!cancelled) setAllowed(result)
    })
    return () => {
      cancelled = true
    }
  }, [email])

  if (!allowed) return null

  const runTest = async (type: string) => {
    setLoading(prev => ({ ...prev, [type]: true }))
    setResults(prev => ({ ...prev, [type]: { ok: true, message: '' } }))
    try {
      await sendDebugNotification(type)
    } catch (err: any) {
      setResults(prev => ({ ...prev, [type]: { ok: false, message: err?.message || 'Failed' } }))
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }))
    }
  }

  return (
    <VBox
      p={2}
      gap={1.5}
      sx={{
        border: '1.5px dashed',
        borderColor: 'warning.dark',
        borderRadius: 2,
        bgcolor: 'warning.light',
        opacity: 0.95
      }}
    >
      <HBox alignItems="center" gap={1}>
        <BugReport sx={{ color: 'warning.dark', fontSize: 20 }} />
        <Typography variant="overline" color="warning.dark" fontWeight={700} lineHeight={1}>
          Notification Debug
        </Typography>
      </HBox>
      <Divider />
      <Table size="small">
        <TableBody>
          {NOTIFICATION_TYPES.map(({ label, type }) => {
            const result = results[type]
            const isLoading = loading[type]
            return (
              <>
                <TableRow key={type} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell
                    sx={{
                      pl: 0,
                      borderBottom: result?.ok === false ? 0 : undefined,
                      width: '100%'
                    }}
                  >
                    <Typography variant="body2" color="text.primary" fontWeight={500}>
                      {label}
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      pr: 0,
                      borderBottom: result?.ok === false ? 0 : undefined,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <ButtonSecondary
                      size="small"
                      onClick={() => runTest(type)}
                      disabled={isLoading}
                      sx={{ fontSize: '11px!important', width: 'auto!important' }}
                    >
                      {isLoading ? <CircularProgress size={12} /> : 'Send test'}
                    </ButtonSecondary>
                  </TableCell>
                </TableRow>
                {result && !result.ok && (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ pb: 1 }}>
                      <Alert severity="error" sx={{ py: 0, fontSize: 11 }}>
                        {result.message}
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}
              </>
            )
          })}
        </TableBody>
      </Table>
    </VBox>
  )
}

export default NotificationDebugPanel
